import { randomUuid } from '~shared/helpers';

const KEY = 'smenadc-device-id';
const DB_NAME = 'smenadc-device';
const DB_STORE = 'device';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 5;

export interface ClientId {
	/** Устойчивый идентификатор устройства */
	client_id: string;
	/** Идентификатор восстановлен из хранилища, а не создан заново */
	has_persistent_storage: boolean;
}

function readLocalStorage(): string | null {
	try {
		return window.localStorage.getItem(KEY);
	} catch {
		return null;
	}
}

function writeLocalStorage(id: string) {
	try {
		window.localStorage.setItem(KEY, id);
	} catch {
		/* приватный режим или отключенное хранилище */
	}
}

function readCookie(): string | null {
	const found = document.cookie.split('; ').find(itm => itm.startsWith(`${KEY}=`));
	return found ? decodeURIComponent(found.slice(KEY.length + 1)) : null;
}

function writeCookie(id: string) {
	document.cookie = `${KEY}=${encodeURIComponent(id)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function withIndexedDb<T>(action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
	return new Promise(resolve => {
		try {
			const request = window.indexedDB.open(DB_NAME, 1);
			request.onupgradeneeded = () => request.result.createObjectStore(DB_STORE);
			request.onerror = () => resolve(null);
			request.onsuccess = () => {
				const db = request.result;
				try {
					const transaction = db.transaction(DB_STORE, 'readwrite');
					const operation = action(transaction.objectStore(DB_STORE));
					operation.onerror = () => resolve(null);
					operation.onsuccess = () => resolve(operation.result ?? null);
					transaction.oncomplete = () => db.close();
				} catch {
					resolve(null);
				}
			};
		} catch {
			resolve(null);
		}
	});
}

/**
 * Идентификатор устройства, продублированный в трех независимых хранилищах.
 * Очистка одного из них (или блокировка cookie) не приводит к потере идентификатора,
 * а приватный режим очищает все три - это отдельно фиксируется через has_persistent_storage.
 */
export async function getClientId(): Promise<ClientId> {
	const fromIndexedDb = await withIndexedDb<string>(store => store.get(KEY) as IDBRequest<string>);
	const stored = readLocalStorage() ?? readCookie() ?? fromIndexedDb;
	const client_id = stored ?? randomUuid();

	writeLocalStorage(client_id);
	writeCookie(client_id);
	await withIndexedDb(store => store.put(client_id, KEY));

	return { client_id, has_persistent_storage: stored !== null };
}
